import { ApiHttpError } from "./errors.js";
/** Minimal in-memory storage used as default. Replace in app code with SecureStore / localStorage. */
class MemoryStorage {
    access = null;
    refresh = null;
    getAccess() { return this.access; }
    getRefresh() { return this.refresh; }
    set(t) { this.access = t.access_token; this.refresh = t.refresh_token; }
    clear() { this.access = null; this.refresh = null; }
}
export class AtlasClient {
    opts;
    fetchImpl;
    storage;
    refreshing = null;
    constructor(opts) {
        this.opts = opts;
        this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
        this.storage = opts.storage ?? new MemoryStorage();
    }
    // ============ Auth ============
    /** Login. Se o usuario tem 2FA ativo, resposta e `{ requires_2fa: true, mfa_token }`
     *  — nenhum token e persistido no storage. O caller precisa chamar verify2fa() com
     *  o mfa_token e o codigo TOTP pra concluir o login. */
    async login(req) {
        const res = await this.request("/v1/auth/login", { method: "POST", body: req, skipAuth: true });
        if ("requires_2fa" in res && res.requires_2fa) {
            // Nao persiste — o access_token ainda nao existe.
            return res;
        }
        const ok = res;
        await this.storage.set({ access_token: ok.access_token, refresh_token: ok.refresh_token, expires_in: ok.expires_in });
        return ok;
    }
    /** Conclui o login apos o passo de 2FA — troca (mfa_token, code) por access+refresh. */
    async verify2fa(mfa_token, code) {
        const res = await this.request("/v1/auth/verify-2fa", { method: "POST", body: { mfa_token, code }, skipAuth: true });
        await this.storage.set({ access_token: res.access_token, refresh_token: res.refresh_token, expires_in: res.expires_in });
        return res;
    }
    /** Self-service do usuario autenticado. Cobre 2FA hoje. */
    me = {
        twoFactor: {
            /** Status atual — { enabled, account }. */
            status: () => this.request("/v1/me/2fa"),
            /** Comeca setup — gera secret novo (nao persiste) + otpauth pro QR. */
            setup: () => this.request("/v1/me/2fa/setup", { method: "POST" }),
            /** Confirma o setup com o codigo TOTP — so aqui persiste. */
            confirm: (code) => this.request("/v1/me/2fa/confirm", { method: "POST", body: { code } }),
            /** Desativa 2FA (precisa do codigo atual). */
            disable: (code) => this.request("/v1/me/2fa/disable", { method: "POST", body: { code } }),
        },
    };
    // ===== Recuperar senha do servidor (fluxo antigo, CPF-only — mantido pra retrocompat) =====
    esqueciSenha = {
        solicitar: (cpf) => this.request("/v1/auth/esqueci-senha/solicitar", { method: "POST", body: { cpf }, skipAuth: true }),
        redefinir: (cpf, codigo, senha) => this.request("/v1/auth/esqueci-senha/redefinir", { method: "POST", body: { cpf, codigo, senha }, skipAuth: true }),
        /** Universal: aceita CPF (servidor) ou e-mail (banco/prefeitura/averbadora). */
        universalSolicitar: (identifier) => this.request("/v1/auth/esqueci-senha/universal-solicitar", { method: "POST", body: { identifier }, skipAuth: true }),
        universalRedefinir: (identifier, codigo, senha) => this.request("/v1/auth/esqueci-senha/universal-redefinir", { method: "POST", body: { identifier, codigo, senha }, skipAuth: true }),
    };
    // ===== Primeiro acesso do servidor (ativa a conta a partir do cadastro da prefeitura) =====
    primeiroAcesso = {
        buscar: (cpf) => this.request("/v1/auth/primeiro-acesso/buscar", { method: "POST", body: { cpf }, skipAuth: true }),
        /** Servidor escolhe email + senha + telefone; codigo vai pra esse email. */
        codigo: (cpf, email, senha, telefone) => this.request("/v1/auth/primeiro-acesso/codigo", { method: "POST", body: { cpf, email, senha, telefone }, skipAuth: true }),
        /** Confirma o codigo — grava passwordHash + o novo email no servidor. */
        confirmar: (cpf, codigo) => this.request("/v1/auth/primeiro-acesso/senha", { method: "POST", body: { cpf, codigo }, skipAuth: true }),
    };
    async logout() {
        try {
            await this.request("/v1/auth/logout", { method: "POST" });
        }
        finally {
            await this.storage.clear();
        }
    }
    // ============ Servidores ============
    async getMyProfile() {
        return this.request("/v1/servidores/me");
    }
    async getMyMargem(opts) {
        return this.request("/v1/servidores/me/margem-consignavel", {
            query: {
                force_refresh: opts?.forceRefresh,
                include: opts?.include?.join(","),
            },
        });
    }
    /** All matrículas of the logged-in servidor, each with full data (margem, contratos, elegíveis). */
    async getMyMatriculas() {
        return this.request("/v1/servidores/me/matriculas");
    }
    // ============ Confirmação por código (step-up, qualquer persona) ============
    // Envia um código de 6 dígitos para o e-mail cadastrado de quem está operando
    // e devolve o destino para o front avisar "código enviado para <e-mail>".
    confirmacao = {
        solicitar: (acao, recurso) => this.request("/v1/confirmacao/solicitar", { method: "POST", body: { acao, recurso } }),
        verificar: (challengeId, codigo) => this.request("/v1/confirmacao/verificar", { method: "POST", body: { challengeId, codigo } }),
    };
    // ============ Portabilidade marketplace ============
    // Averbadora armazena a intencao (dados vindos do contrato origem). Bancos
    // concorrentes veem e ofertam. Servidor aceita a melhor.
    portabilidade = {
        // === Servidor ===
        minhas: () => this.request("/v1/servidores/me/portabilidade"),
        publicar: (adf) => this.request("/v1/servidores/me/portabilidade", { method: "POST", body: { adf } }),
        cancelar: (id) => this.request(`/v1/servidores/me/portabilidade/${id}/cancelar`, { method: "POST" }),
        aceitar: (id, ofertaId) => this.request(`/v1/servidores/me/portabilidade/${id}/aceitar`, { method: "POST", body: { ofertaId } }),
        // === Banco ===
        oportunidadesBanco: () => this.request("/v1/portal/banco/portabilidade"),
        ofertar: (id, body) => this.request(`/v1/portal/banco/portabilidade/${id}/ofertar`, { method: "POST", body }),
        // === Averbadora (visao global) ===
        todas: () => this.request("/v1/admin/portabilidade"),
    };
    // ============ Servidor (marketplace + demais consultas do proprio servidor) ============
    servidor = {
        /** Marketplace de ofertas — derivado das tabelas de emprestimo publicadas pelos bancos.
         *  Passe `matricula` pra filtrar so ofertas dos convenios da prefeitura dessa matricula
         *  (evita servidor de Palhoca ver oferta que so vale em Florianopolis). */
        ofertas: (matricula) => this.request(matricula ? `/v1/servidores/me/ofertas?matricula=${encodeURIComponent(matricula)}` : "/v1/servidores/me/ofertas"),
        /** Ofertas ativas criadas pelos bancos que casam com o perfil do servidor.
         *  Passe `matricula` pra respeitar a matricula ativa no switcher (senao usa
         *  a do login, o que confunde quem tem acumulacao de cargos). */
        getMyOfertasBanco: (matricula) => this.request(matricula ? `/v1/servidores/me/ofertas-banco?matricula=${encodeURIComponent(matricula)}` : "/v1/servidores/me/ofertas-banco"),
        /** Beneficios/descontos da prefeitura do servidor. Passe matricula pra respeitar
         *  o switcher (senao usa a prefeitura do JWT, que fixa a matricula do login). */
        getMyBeneficios: (categoria, matricula) => this.request("/v1/servidores/me/beneficios", {
            query: {
                ...(categoria ? { categoria } : {}),
                ...(matricula ? { matricula } : {}),
            },
        }),
        /** Servidor solicita uma proposta (pré-reserva) — CRIA no store do banco (o banco recebe). */
        /** Status da SENHA DE TRANSACAO — segundo fator separado da senha de login
         *  usado quando a prefeitura marca exigeSenhaProposta ou exigeSenhaAverbacao.
         *  Frontend usa pra decidir se abre o modal obrigatorio de cadastro. */
        transactionPasswordStatus: () => this.request("/v1/servidores/me/transaction-password/status"),
        /** Define ou troca a senha de transacao. Politica: 8+ chars, com maiuscula,
         *  minuscula, digito e caractere especial. Troca exige senhaAtual. Primeira
         *  vez (nao ha hash ainda) so precisa da novaSenha. */
        setTransactionPassword: (body) => this.request("/v1/servidores/me/transaction-password", { method: "POST", body }),
        criarProposta: (input, opts) => this.request("/v1/servidores/me/propostas", { method: "POST", body: input, signal: opts?.signal }),
        /** Registra o clique do servidor no botao "Acessar" de um beneficio.
         *  Best-effort — o botao abre a URL de qualquer jeito (nao bloqueia se falhar). */
        registrarCliqueBeneficio: (beneficioId, input) => this.request(`/v1/servidores/me/beneficios/${encodeURIComponent(beneficioId)}/clique`, { method: "POST", body: input ?? {} }),
        /** Solicita cotacao de telemedicina — a averbadora recebe os dados do servidor
         *  (principalmente o telefone) pra entrar em contato e formalizar o plano. */
        solicitarCotacaoTelemedicina: (input) => this.request("/v1/servidores/me/telemedicina/cotacao", { method: "POST", body: input ?? {} }),
        /** Cotacoes de telemedicina DO PROPRIO servidor — pra esconder o botao (mostra
         *  "em analise") e listar na aba Em Analise. */
        minhasCotacoesTelemedicina: () => this.request("/v1/servidores/me/telemedicina/cotacoes"),
        /** Vitrine (carrossel) mostrada no dashboard do servidor — banners ativos
         *  cadastrados pela averbadora em /averbadora/vitrine. */
        vitrine: () => this.request("/v1/servidores/me/vitrine"),
        /** Registra impressao de um banner (fire-and-forget do card visto). */
        vitrineImpressao: (bannerId) => this.request(`/v1/servidores/me/vitrine/${bannerId}/impressao`, { method: "POST" }),
        /** Registra clique num banner — alimenta a receita CPC da averbadora. */
        vitrineClique: (bannerId) => this.request(`/v1/servidores/me/vitrine/${bannerId}/clique`, { method: "POST" }),
        /** Solicita cartao consignado ou cartao beneficio. Nao cria contrato tradicional
         *  (o modelo ainda so aceita EMPRESTIMO/REFIN/ECONSIGNADO) — registra a
         *  solicitacao pra averbadora e devolve um protocolo. O banco recebe pra
         *  emitir/ativar o cartao via canal proprio (padrao do mercado). */
        solicitarCartao: (input, opts) => this.request("/v1/servidores/me/cartoes", { method: "POST", body: input, signal: opts?.signal }),
        /** Propostas/pré-reservas do próprio servidor (mesma fonte que o banco lê).
         *  Filtra pela matrícula ativa quando informada — evita misturar histórico
         *  entre matrículas de servidor com acumulação de cargos. */
        /** Comunicados publicados pela averbadora com publico=servidor (usados no carrossel do dashboard). */
        comunicados: () => this.request("/v1/servidores/me/comunicados"),
        /** Baixa o CCB (contrato assinado que o banco anexou) como Blob. Envia
         *  Authorization Bearer. Lanca erro com status HTTP e reason (extraido do
         *  body JSON quando o backend explica a causa) — o front distingue os
         *  varios 404 (banco nao anexou vs contrato nao achado vs arquivo sumiu). */
        baixarContratoCcb: async (adf) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/servidores/me/contratos/${adf}/ccb.pdf`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                let reason;
                try {
                    const body = await res.json();
                    reason = body?.reason;
                }
                catch { /* body nao era JSON — sem reason */ }
                const err = new Error(`Falha ao baixar contrato (${res.status}${reason ? ` — ${reason}` : ""})`);
                err.status = res.status;
                if (reason)
                    err.reason = reason;
                throw err;
            }
            return await res.blob();
        },
        propostas: (matricula) => this.request("/v1/servidores/me/propostas", { query: matricula ? { matricula } : undefined }),
        // Averbacoes iniciadas pelo banco pendentes de confirmacao do servidor
        // (fluxo "empréstimo iniciado pelo banco" quando prefeitura exige senha).
        averbacoesPendentes: () => this.request("/v1/servidores/me/averbacoes-pendentes"),
        confirmarAverbacaoPendente: (adf, senha) => this.request(`/v1/servidores/me/averbacoes-pendentes/${adf}/confirmar`, { method: "POST", body: { senha } }),
        rejeitarAverbacaoPendente: (adf, senha, motivo) => this.request(`/v1/servidores/me/averbacoes-pendentes/${adf}/rejeitar`, { method: "POST", body: { senha, motivo } }),
        /** Redefinicao direta (sem codigo por e-mail) — DEPRECIADA em favor de
         *  pedirCodigoSenha + confirmarNovaSenha. Mantida no SDK pra outros
         *  clientes (ex.: mobile) que ainda usem. */
        redefinirSenha: (input) => this.request("/v1/servidores/me/senha/redefinir", {
            method: "POST",
            body: { senha_atual: input.senhaAtual, nova_senha: input.novaSenha },
        }),
        /** Passo 1 do fluxo com verificacao por e-mail: pede o backend gerar um
         *  codigo de 6 digitos e enviar pro e-mail cadastrado. Modo teste (SMTP
         *  nao configurado) devolve o codigo direto no campo `codigo_teste`. */
        pedirCodigoSenha: () => this.request("/v1/servidores/me/codigo", { method: "POST" }),
        /** Passo 2: valida senha atual + codigo do e-mail e persiste a nova senha.
         *  Backend recusa se codigo estiver invalido/expirado (401) ou senha atual
         *  errada (401). Depois de sucesso o codigo e removido do KV. */
        confirmarNovaSenha: (input) => this.request("/v1/servidores/me/senha", {
            method: "POST",
            body: { senha_atual: input.senhaAtual, nova_senha: input.novaSenha, codigo: input.codigo },
        }),
        /** Cancela um codigo pendente (deleta do KV). Chamado quando o usuario
         *  clica "Cancelar" apos ja ter pedido o codigo — evita reuso posterior.
         *  Vale tanto pro fluxo de senha quanto pro fluxo de contato — mesma chave
         *  no KV (`chg:${cpf}`). */
        cancelarCodigoSenha: () => this.request("/v1/servidores/me/codigo", { method: "DELETE" }),
        /** Passo 2 do fluxo de contato: valida o codigo + persiste e-mail/telefone.
         *  Fluxo:
         *  1) pedirCodigoSenha() envia o codigo (endpoint compartilhado com senha).
         *  2) Usuario digita.
         *  3) atualizarContato({ codigo, email, telefone }) grava.
         *  Backend recusa se codigo estiver invalido/expirado (401). Sucesso remove
         *  o codigo do KV automaticamente. */
        atualizarContato: (input) => this.request("/v1/servidores/me/contato", {
            method: "POST",
            body: { codigo: input.codigo, email: input.email, telefone: input.telefone },
        }),
        /** Le o termo renderizado (placeholders substituidos com vars). Fonte: /averbadora/termos. */
        getTermo: (tipo, vars) => {
            const query = {};
            if (vars && Object.keys(vars).length > 0)
                query.vars = JSON.stringify(vars);
            return this.request(`/v1/servidores/me/termos/${tipo}`, { query });
        },
        /** Lista todos os termos ativos em vigencia (metadados, sem corpo). Usado
         *  na tela Conta > Suporte pra o servidor conferir o que esta vigente. */
        listTermos: () => this.request("/v1/servidores/me/termos"),
        /** Info de suporte (email/whatsapp/horario/frase de abertura) que a averbadora
         *  edita em /averbadora/suporte. Servidor usa no modal de Conta > Suporte. */
        getSuporte: () => this.request("/v1/servidores/me/suporte"),
    };
    // ============ Portal Banco ============
    banco = {
        me: () => this.request("/v1/portal/banco/me"),
        convenios: () => this.request("/v1/portal/banco/convenios"),
        setConvenioAtivo: (convenioId) => this.request("/v1/portal/banco/convenio-ativo", { method: "POST", body: { convenioId } }),
        visaoGeral: () => this.request("/v1/portal/banco/visao-geral"),
        comunicados: () => this.request("/v1/portal/banco/comunicados"),
        margemBuscar: (input) => this.request("/v1/portal/banco/margem/buscar", { method: "POST", body: input }),
        margemExemplos: () => this.request("/v1/portal/banco/margem/exemplos"),
        margemCalcular: (idMatricula, input) => this.request(`/v1/portal/banco/margem/${idMatricula}/calcular`, { method: "POST", body: input }),
        // Averbacao INICIADA PELO BANCO (emprestimo, cartao consignado ou beneficio).
        // Se a prefeitura marcou exigeSenhaAverbacao=true, contrato vira "Aguardando
        // Confirmacao do Servidor" — o servidor precisa confirmar com senha em
        // /me/averbacoes-pendentes/:adf/confirmar. Se false, ja vira Ativo direto.
        averbarDireto: (idMatricula, input) => this.request(`/v1/portal/banco/margem/${idMatricula}/averbar`, { method: "POST", body: input }),
        // Editar proposta: fluxo de negociacao offline. Banco liga pro servidor
        // pra negociar valor/parcelas, servidor concorda, banco edita aqui e
        // depois aprova. Servidor recebe notif do que foi acordado.
        editarProposta: (adf, input) => this.request(`/v1/portal/banco/contratos/${adf}/editar`, { method: "POST", body: input }),
        contratos: (filtros = {}) => this.request("/v1/portal/banco/contratos", {
            query: {
                colaborador: filtros.colaborador,
                ...(filtros.incluirTodosConvenios ? { incluir_todos_convenios: "true" } : {}),
                ...(filtros.situacao ? Object.fromEntries(filtros.situacao.map((s, i) => [`situacao_${i}`, s])) : {}),
            },
        }),
        getContrato: (adf) => this.request(`/v1/portal/banco/contratos/${adf}`),
        averbar: (tipo, body) => this.request(`/v1/portal/banco/contratos/averbar/${tipo}`, { method: "POST", body }),
        reservar: (tipo, body) => this.request(`/v1/portal/banco/contratos/reservar/${tipo}`, { method: "POST", body }),
        acao: (adf, acao, body) => this.request(`/v1/portal/banco/contratos/${adf}/${acao}`, { method: "POST", body: body ?? {} }),
        // Contratos com falha em folha reportada pela averbadora — banco precisa tratar.
        listContratosFalha: () => this.request("/v1/portal/banco/falhas"),
        tratarFalha: (adf, acao, motivo) => this.request(`/v1/portal/banco/tratar-falha/${adf}`, {
            method: "POST",
            body: { acao, motivo },
        }),
        comprovanteUrl: (adf) => new URL(`/v1/portal/banco/contratos/${adf}/comprovante.pdf`, this.opts.baseUrl).toString(),
        // Cadastros
        listTabelas: () => this.request("/v1/portal/banco/cadastros/tabela-emprestimos"),
        getTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}`),
        upsertTabela: (body) => this.request("/v1/portal/banco/cadastros/tabela-emprestimos", { method: "POST", body }),
        removerTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}`, { method: "DELETE" }),
        /** Faz upload da CCB assinada (PDF) pro R2. Retorna a chave persistida. */
        uploadCcb: async (adf, file) => {
            const fd = new FormData();
            fd.append("adf", adf);
            fd.append("file", file);
            return this.request("/v1/portal/banco/ccb/upload", { method: "POST", body: fd, isFormData: true });
        },
        /** URL absoluta para visualizar a CCB (requer sessao do proprio banco). */
        ccbUrl: (key) => new URL(`/v1/portal/banco/ccb/${key}`, this.opts.baseUrl).toString(),
        /** Baixa o CCB anexado como Blob (envia Authorization). Necessario
         *  porque o endpoint exige Bearer — abrir a ccbUrl direto no <a href>
         *  volta 401. Use com URL.createObjectURL pra abrir/salvar. */
        fetchCcbBlob: async (key) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/portal/banco/ccb/${key}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar CCB (${res.status})`);
            return await res.blob();
        },
        reativarTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}/reativar`, { method: "POST" }),
        listUsuarios: (q) => this.request("/v1/portal/banco/cadastros/usuarios", { query: q ?? {} }),
        getUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}`),
        upsertUsuario: (body) => this.request("/v1/portal/banco/cadastros/usuarios", { method: "POST", body }),
        removerUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}`, { method: "DELETE" }),
        reativarUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}/reativar`, { method: "POST" }),
        /** Devolve o CPF completo do usuario (acesso registrado em audit log no servidor). */
        revealUsuarioCpf: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}/cpf`),
        perfilPresetsBanco: () => this.request("/v1/portal/banco/perfil-presets"),
        criarPerfilPresetBanco: (body) => this.request("/v1/portal/banco/perfil-presets", { method: "POST", body }),
        // Relatorios
        relatorioConsignacoes: (q) => this.request("/v1/portal/banco/relatorios/consignacoes", { query: q ?? {} }),
        relatorioFaturamento: () => this.request("/v1/portal/banco/relatorios/faturamento"),
        // Bate de carteira mensal (escopado ao banco logado — jwt.banco_id).
        // Substitui o baterCarteira() client-side que era pseudo-random.
        bateCarteira: (body) => this.request("/v1/portal/banco/bate-carteira", { method: "POST", body }),
        // ===== Ofertas de credito (banco -> servidores) =====
        ofertas: {
            list: () => this.request("/v1/portal/banco/ofertas"),
            upsert: (body) => this.request("/v1/portal/banco/ofertas", { method: "POST", body }),
            pausar: (id) => this.request(`/v1/portal/banco/ofertas/${id}/pausar`, { method: "PATCH" }),
            reativar: (id) => this.request(`/v1/portal/banco/ofertas/${id}/reativar`, { method: "PATCH" }),
        },
    };
    // ============ Admin (Averbadora) ============
    admin = {
        me: () => this.request("/v1/admin/me"),
        // ===== IA (OpenAI) =====
        aiConfig: () => this.request("/v1/admin/ai/config"),
        aiSaveKey: (apiKey) => this.request("/v1/admin/ai/config", { method: "PUT", body: { apiKey } }),
        aiClearKey: () => this.request("/v1/admin/ai/config", { method: "DELETE" }),
        aiTest: () => this.request("/v1/admin/ai/test", { method: "POST" }),
        // ===== SMTP (e-mails de confirmação) =====
        smtpConfig: () => this.request("/v1/admin/smtp/config"),
        smtpSave: (input) => this.request("/v1/admin/smtp/config", { method: "PUT", body: input }),
        smtpClear: () => this.request("/v1/admin/smtp/config", { method: "DELETE" }),
        smtpTest: (to) => this.request("/v1/admin/smtp/test", { method: "POST", body: { to } }),
        // ===== Config de suporte (info exibida ao servidor em Conta > Suporte) =====
        suporteConfig: () => this.request("/v1/admin/suporte-config"),
        suporteSave: (input) => this.request("/v1/admin/suporte-config", { method: "PUT", body: input }),
        aiNormalizeCsv: (body) => this.request("/v1/admin/ai/normalize-csv", { method: "POST", body }),
        dashboard: () => this.request("/v1/admin/dashboard"),
        listBancos: () => this.request("/v1/admin/bancos"),
        getBanco: (id) => this.request(`/v1/admin/bancos/${id}`),
        upsertBanco: (b) => this.request("/v1/admin/bancos", { method: "POST", body: b }),
        testarBanco: (id) => this.request(`/v1/admin/bancos/${id}/testar-conexao`, { method: "POST" }),
        resetBancoPassword: (id, password) => this.request(`/v1/admin/bancos/${id}/reset-password`, { method: "POST", body: { password } }),
        deleteBanco: (id, confirm) => this.request(`/v1/admin/bancos/${id}`, { method: "DELETE", body: confirm }),
        listPrefeituras: () => this.request("/v1/admin/prefeituras"),
        upsertPrefeitura: (p) => this.request("/v1/admin/prefeituras", { method: "POST", body: p }),
        /** Zera a base de prefeituras. Exige senha compartilhada (env
         *  ADMIN_PURGE_PASSWORD). Nao cascatea pra convenios/folhas. */
        limparBasePrefeituras: (senha) => this.request("/v1/admin/prefeituras/limpar-base", { method: "POST", body: { senha } }),
        sincronizarPrefeitura: (id) => this.request(`/v1/admin/prefeituras/${id}/sincronizar`, { method: "POST" }),
        /** Consulta CNPJ via BrasilAPI (Receita + Junta Comercial). Usado nos
         *  modais de cadastro de prefeitura E de banco — endpoint reaproveitado. */
        consultarCnpjPrefeitura: (cnpj) => this.request(`/v1/admin/prefeituras/consulta-cnpj/${encodeURIComponent(cnpj.replace(/\D/g, ""))}`),
        /** Zera a base de bancos. Exige senha compartilhada (env ADMIN_PURGE_PASSWORD). */
        limparBaseBancos: (senha) => this.request("/v1/admin/bancos/limpar-base", { method: "POST", body: { senha } }),
        /** Destrava os endpoints destrutivos (kill-switch) por N segundos. Exige a
         *  senha ADMIN_PURGE_PASSWORD. Usado antes de rodar um delete cirurgico. */
        destravarDestrutivos: (senha, durationSec = 120) => this.request("/v1/admin/destructive-lock/unlock", { method: "POST", body: { senha, durationSec } }),
        /** Apaga servidores por NOME (ex.: "ZZ OBSOLETA - NAO USAR", "EXEMPLO - ..."):
         *  so a ficcao sai, os reais ficam. Requer o kill-switch destravado + senha. */
        deleteServidoresPorNome: (nomes, senha) => this.request("/v1/admin/servidores/delete-by-nome", { method: "POST", body: { nomes, senha } }),
        /** Delete cirurgico por matricula(s). Requer kill-switch destravado + senha
         *  ADMIN_PURGE_PASSWORD. Cascade: remove servidor + contratos + tombamento
         *  (linhas do jsonb; lote inteiro se ficar vazio) + pre-reservas +
         *  portabilidades + cotacoes telemedicina + beneficio-cliques/intencoes +
         *  servidor_status do PG e da memoria. */
        deleteServidoresPorMatricula: (matriculas, senha) => this.request("/v1/admin/servidores/delete-matriculas", { method: "POST", body: { matriculas, senha } }),
        resetPrefeituraPassword: (id, password) => this.request(`/v1/admin/prefeituras/${id}/reset-password`, { method: "POST", body: { password } }),
        deletePrefeitura: (id, confirm) => this.request(`/v1/admin/prefeituras/${id}`, { method: "DELETE", body: confirm }),
        // Step-up por email para acoes destrutivas. Retorna codigoDemo pois nao ha provider de email.
        solicitarConfirmacao: (acao, recurso) => this.request("/v1/admin/confirmacao/solicitar", { method: "POST", body: { acao, recurso } }),
        listConvenios: () => this.request("/v1/admin/convenios"),
        upsertConvenio: (body) => this.request("/v1/admin/convenios", { method: "POST", body }),
        deleteConvenio: (id) => this.request(`/v1/admin/convenios/${id}`, { method: "DELETE" }),
        reativarConvenio: (id) => this.request(`/v1/admin/convenios/${id}/reativar`, { method: "POST" }),
        /** Hard-delete permanente. So convenio INATIVO + sem contratos. */
        deleteConvenioHard: (id) => this.request(`/v1/admin/convenios/${id}/hard`, { method: "DELETE" }),
        getConvenioConfig: (id) => this.request(`/v1/admin/convenios/${id}/config`),
        listConveniosConfigs: () => this.request("/v1/admin/convenios-configs"),
        upsertConvenioConfig: (id, body) => this.request(`/v1/admin/convenios/${id}/config`, { method: "POST", body }),
        // ID único
        listIdUnicoConfigs: () => this.request("/v1/admin/id-unico/configs"),
        upsertIdUnicoConfig: (body) => this.request("/v1/admin/id-unico/configs", { method: "POST", body }),
        issueIdUnico: (prefeituraId) => this.request("/v1/admin/id-unico/issue", { method: "POST", body: { prefeituraId } }),
        // Pré-reservas
        listPreReservas: (q) => this.request("/v1/admin/pre-reservas", { query: q ?? {} }),
        cancelarPreReserva: (id, motivo) => this.request(`/v1/admin/pre-reservas/${id}/cancelar`, { method: "POST", body: { motivo } }),
        sweepPreReservas: () => this.request("/v1/admin/pre-reservas/sweep", { method: "POST" }),
        // Tombamento
        listTombamentoLotes: (q) => this.request("/v1/admin/tombamento/lotes", { query: q ?? {} }),
        listTombamentoLinhas: (loteId) => this.request(`/v1/admin/tombamento/lotes/${loteId}/linhas`),
        importarTombamento: (body) => this.request("/v1/admin/tombamento/importar", { method: "POST", body }),
        tombamentoCsvTemplateUrl: () => `${this.opts.baseUrl}/v1/admin/tombamento/csv-template`,
        // Bate-de-carteira
        bateCarteira: (body) => this.request("/v1/admin/bate-carteira", { method: "POST", body }),
        bateCarteiraCsvUrl: () => `${this.opts.baseUrl}/v1/admin/bate-carteira`,
        // Auditoria
        listAuditoria: (q) => this.request("/v1/admin/auditoria", { query: q ?? {} }),
        // Cotacoes de telemedicina solicitadas pelos servidores (banner de Beneficios).
        // A averbadora ve os dados do servidor — principalmente o telefone — pra formalizar.
        listTelemedicinaCotacoes: () => this.request("/v1/admin/telemedicina/cotacoes"),
        ativarCotacaoTelemedicina: (id) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/ativar`, { method: "POST" }),
        cancelarCotacaoTelemedicina: (id) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/cancelar`, { method: "POST" }),
        purgeTelemedicinaCotacoes: () => this.request("/v1/admin/telemedicina/cotacoes/purge", { method: "POST" }),
        /** Anexa o contrato da telemedicina (R2). Sem ele o plano nao pode ser ativado. */
        uploadContratoTelemedicina: (id, file) => {
            const fd = new FormData();
            fd.append("file", file);
            return this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, { method: "POST", body: fd, isFormData: true });
        },
        /** Remove o contrato anexado (arquivo errado) — volta a bloquear a ativacao. */
        removerContratoTelemedicina: (id) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, { method: "DELETE" }),
        /** Baixa o contrato anexado da cotacao (envia Authorization). */
        fetchContratoTelemedicinaBlob: async (id) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar contrato (${res.status})`);
            return await res.blob();
        },
        // Templates de TERMOS (aceite in-app do servidor + anuencia da prefeitura)
        listTermos: () => this.request("/v1/admin/termos"),
        upsertTermo: (tipo, patch) => this.request(`/v1/admin/termos/${tipo}`, { method: "POST", body: patch }),
        // Perfis admin
        listPerfisAdmin: () => this.request("/v1/admin/perfis"),
        upsertPerfilAdmin: (body) => this.request("/v1/admin/perfis", { method: "POST", body }),
        perfilPresetsAdmin: () => this.request("/v1/admin/perfil-presets"),
        criarPerfilPresetAdmin: (body) => this.request("/v1/admin/perfil-presets", { method: "POST", body }),
        rotate2FA: (id) => this.request(`/v1/admin/perfis/${id}/2fa/rotate`, { method: "POST" }),
        disable2FA: (id) => this.request(`/v1/admin/perfis/${id}/2fa/disable`, { method: "POST" }),
        deletePerfilAdmin: (id) => this.request(`/v1/admin/perfis/${id}`, { method: "DELETE" }),
        reativarPerfilAdmin: (id) => this.request(`/v1/admin/perfis/${id}/reativar`, { method: "POST" }),
        // ===== ADF — averbadora aplica/reporta falha (prefeitura so recebe) =====
        adfCompetencias: () => this.request("/v1/admin/adf/competencias"),
        adfList: (q) => this.request("/v1/admin/adf", { query: q ?? {} }),
        confirmarAdfAdmin: (ids) => this.request("/v1/admin/adf/confirmar", { method: "POST", body: { ids } }),
        reportarFalhaAdfAdmin: (ids, motivo) => this.request("/v1/admin/adf/falha", { method: "POST", body: { ids, motivo } }),
        // ===== Beneficios / descontos por prefeitura =====
        beneficios: {
            list: () => this.request("/v1/admin/beneficios"),
            upsert: (body) => this.request("/v1/admin/beneficios", { method: "POST", body }),
            pausar: (id) => this.request(`/v1/admin/beneficios/${id}/pausar`, { method: "PATCH" }),
            reativar: (id) => this.request(`/v1/admin/beneficios/${id}/reativar`, { method: "PATCH" }),
            /** Interessados (servidores que clicaram no botao Acessar). ?beneficioId filtra. */
            interessados: (beneficioId) => this.request("/v1/admin/beneficios/interessados", { query: beneficioId ? { beneficioId } : {} }),
            /** Contagem de interessados por beneficio (badge nas listas). */
            interessadosResumo: () => this.request("/v1/admin/beneficios/interessados/resumo"),
        },
        // ===== Modelos de e-mail (editaveis pela averbadora) =====
        emailTemplates: {
            list: () => this.request("/v1/admin/email-templates"),
            upsert: (body) => this.request("/v1/admin/email-templates", { method: "POST", body }),
            remover: (id) => this.request(`/v1/admin/email-templates/${id}`, { method: "DELETE" }),
            /** Envia um teste real via SMTP configurado. Se `vars` for omitido/vazio,
             *  o backend preenche automaticamente com dados de exemplo realistas. */
            enviarTeste: (id, body) => this.request(`/v1/admin/email-templates/${id}/test`, { method: "POST", body }),
            /** Pre-preenchimento de variaveis com dados realistas — usado pelo
             *  modal de teste pra popular os inputs sem o operador digitar. */
            previewVars: (id) => this.request(`/v1/admin/email-templates/${id}/preview-vars`),
        },
        listServidores: (q) => this.request("/v1/admin/servidores", { query: q ?? {} }),
        updateServidor: (matricula, body) => this.request(`/v1/admin/servidores/${matricula}`, { method: "PATCH", body }),
        /** Le a config de campos de servidor da prefeitura (cria default se ainda nao existe). */
        getServidorCamposConfig: (prefeituraId) => this.request(`/v1/admin/servidores/campos-config/${prefeituraId}`),
        /** Salva a config de campos. Backend re-injeta cpf/matricula/email como travados. */
        updateServidorCamposConfig: (prefeituraId, campos) => this.request(`/v1/admin/servidores/campos-config/${prefeituraId}`, { method: "PUT", body: { campos } }),
        /** URL do CSV modelo dinamico da prefeitura (colunas seguem a config).
         *  Se `presetKey` for informado, usa o snapshot daquele preset custom
         *  (o CSV replica exatamente o estado dos sistema no momento em que o
         *  preset foi criado + o proprio custom). */
        servidoresCsvTemplateUrl: (prefeituraId, presetKey) => {
            const base = `${this.opts.baseUrl}/v1/admin/servidores/csv-template?prefeituraId=${prefeituraId}`;
            return presetKey ? `${base}&preset=${encodeURIComponent(presetKey)}` : base;
        },
        listFolhas: () => this.request("/v1/admin/folhas"),
        upsertFolha: (f) => this.request("/v1/admin/folhas", { method: "POST", body: f }),
        consolidarFolha: (id) => this.request(`/v1/admin/folhas/${id}/consolidar`, { method: "POST" }),
        deleteFolha: (id) => this.request(`/v1/admin/folhas/${id}`, { method: "DELETE" }),
        // Visao global de contratos averbados (ADF averbadora — todos os bancos).
        contratos: () => this.request("/v1/admin/contratos"),
        listComunicados: () => this.request("/v1/admin/comunicados"),
        upsertComunicado: (body) => this.request("/v1/admin/comunicados", { method: "POST", body }),
        moveComunicado: (id, direction) => this.request(`/v1/admin/comunicados/${id}/mover`, { method: "POST", body: { direction } }),
        /** Reordena a lista inteira em um POST — usado pelo drag-and-drop. */
        reordenarComunicados: (ids) => this.request("/v1/admin/comunicados/reordenar", { method: "POST", body: { ids } }),
        deleteComunicado: (id) => this.request(`/v1/admin/comunicados/${id}`, { method: "DELETE" }),
        health: () => this.request("/v1/admin/health"),
        impersonateServidor: (matricula) => this.request(`/v1/admin/impersonate/servidor/${encodeURIComponent(matricula)}`, { method: "POST", body: {} }),
        verify: (opts) => this.request("/v1/admin/verify", { query: opts?.fresh ? { fresh: "1" } : {} }),
        logs: (q) => this.request("/v1/admin/logs", { query: q ?? {} }),
        listVitrine: () => this.request("/v1/admin/vitrine"),
        upsertBanner: (b) => this.request("/v1/admin/vitrine", { method: "POST", body: b }),
        // === API tokens ===
        listApiTokens: (filter) => this.request("/v1/admin/api-tokens", { query: filter ?? {} }),
        createApiToken: (body) => this.request("/v1/admin/api-tokens", { method: "POST", body }),
        // Pausa/reativa UM token (manual, reversível). Não apaga nem toca no perfil/parceria dono.
        pauseApiToken: (id, paused) => this.request(`/v1/admin/api-tokens/${id}/pause`, { method: "PATCH", body: { paused } }),
        // === Webhooks ===
        listWebhooks: (environment) => this.request("/v1/admin/webhooks", { query: environment ? { environment } : {} }),
        createWebhook: (body) => this.request("/v1/admin/webhooks", { method: "POST", body }),
        // Webhooks não são apagados/pausados manualmente: seguem o status do banco dono (cascade).
        webhookDeliveries: (id) => this.request(`/v1/admin/webhooks/${id}/deliveries`),
        fireWebhook: (body) => this.request("/v1/admin/webhooks/fire", { method: "POST", body }),
        testWebhook: (id) => this.request(`/v1/admin/webhooks/${id}/test`, { method: "POST" }),
        // === CSV import ===
        importCsv: (entity, csv, params) => this.request(`/v1/admin/${entity}/importar`, { method: "POST", body: { csv }, query: params }),
        csvTemplateUrl: (entity) => `${this.opts.baseUrl}/v1/admin/${entity}/csv-template`,
    };
    // ============ Prefeitura (portal completo) ============
    prefeitura = {
        me: () => this.request("/v1/prefeitura/me"),
        getConfig: () => this.request("/v1/prefeitura/config"),
        getServidorCamposConfig: () => this.request("/v1/prefeitura/servidor-campos-config"),
        setConfig: (body) => this.request("/v1/prefeitura/config", { method: "POST", body }),
        dashboard: () => this.request("/v1/prefeitura/dashboard"),
        // Servidores (passos 3 e 6)
        servidores: (query) => this.request("/v1/prefeitura/servidores", { query: query ?? {} }),
        importarServidores: (csv) => this.request("/v1/prefeitura/servidores/importar", { method: "POST", body: { csv } }),
        editarServidor: (matricula, patch) => this.request(`/v1/prefeitura/servidores/${matricula}`, { method: "PATCH", body: patch }),
        // prefeituraId opcional: quando passado, o header do CSV vem da config de
        // campos que a averbadora definiu pra prefeitura. O frontend pega o id do
        // servidorCamposConfig() e repassa aqui.
        servidoresCsvTemplateUrl: (prefeituraId) => `${this.opts.baseUrl}/v1/prefeitura/servidores/csv-template${prefeituraId != null ? `?prefeituraId=${prefeituraId}` : ""}`,
        /** Campos de servidor configurados pela averbadora pra esta prefeitura —
         *  usado pra montar o cabecalho do CSV (download + import) dinamicamente. */
        servidorCamposConfig: () => this.request("/v1/prefeitura/servidores/campos-config"),
        // Folha (passo 4)
        folhas: () => this.request("/v1/prefeitura/folhas"),
        abrirFolha: (body) => this.request("/v1/prefeitura/folhas", { method: "POST", body }),
        atualizarFolha: (id, body) => this.request(`/v1/prefeitura/folhas/${id}`, { method: "PATCH", body }),
        excluirFolha: (id) => this.request(`/v1/prefeitura/folhas/${id}`, { method: "DELETE" }),
        movimentacoes: (folhaId) => this.request(`/v1/prefeitura/folhas/${folhaId}/movimentacoes`),
        enviarMovimentacao: (folhaId, csv) => this.request(`/v1/prefeitura/folhas/${folhaId}/movimentacao`, { method: "POST", body: { csv } }),
        movimentacaoCsvTemplateUrl: () => `${this.opts.baseUrl}/v1/prefeitura/folhas/movimentacao/csv-template`,
        // Convênios + config (passo 5)
        convenios: () => this.request("/v1/prefeitura/convenios"),
        convenioConfig: (id) => this.request(`/v1/prefeitura/convenios/${id}/config`),
        salvarConvenioConfig: (id, body) => this.request(`/v1/prefeitura/convenios/${id}/config`, { method: "PUT", body }),
        // Contratos + tombamento (passo 7)
        contratos: () => this.request("/v1/prefeitura/contratos"),
        /** Baixa o CCB (contrato assinado pelo banco, PDF do R2) de um contrato
         *  averbado. Backend valida que o contrato pertence a esta prefeitura. */
        baixarContratoCcb: async (adf) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/prefeitura/contratos/${encodeURIComponent(adf)}/ccb.pdf`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                // Preserva o reason do backend pra UI diferenciar 'ccb_nao_anexado' vs
                // 'arquivo_nao_encontrado_no_r2' vs 'contrato_nao_e_desta_prefeitura'.
                let reason;
                try {
                    const body = (await res.json());
                    reason = body.error?.code;
                }
                catch { /* ignore */ }
                const err = new Error(`Falha ao baixar CCB (${res.status})`);
                err.status = res.status;
                err.reason = reason;
                throw err;
            }
            return await res.blob();
        },
        tombamentoLotes: () => this.request("/v1/prefeitura/tombamento/lotes"),
        tombamentoLinhas: (loteId) => this.request(`/v1/prefeitura/tombamento/lotes/${loteId}/linhas`),
        importarTombamento: (csv, competencia) => this.request("/v1/prefeitura/tombamento/importar", { method: "POST", body: { csv }, query: { competencia } }),
        tombamentoCsvTemplateUrl: () => `${this.opts.baseUrl}/v1/prefeitura/tombamento/csv-template`,
        // ADF / descontos em folha (passo 8)
        adfCompetencias: () => this.request("/v1/prefeitura/adf/competencias"),
        adf: (competencia) => this.request("/v1/prefeitura/adf", { query: competencia ? { competencia } : {} }),
        adfCsvUrl: (competencia) => `${this.opts.baseUrl}/v1/prefeitura/adf/${competencia}/download.csv`,
        adfPdfUrl: (competencia) => `${this.opts.baseUrl}/v1/prefeitura/adf/${competencia}/lote.pdf`,
        confirmarAdf: (ids) => this.request("/v1/prefeitura/adf/confirmar", { method: "POST", body: { ids } }),
        reportarFalhaAdf: (ids, motivo) => this.request("/v1/prefeitura/adf/falha", { method: "POST", body: { ids, motivo } }),
        // Relatórios (passo 9)
        relServidoresPorVinculo: () => this.request("/v1/prefeitura/relatorios/servidores-por-vinculo"),
        relMargemMedia: () => this.request("/v1/prefeitura/relatorios/margem-media"),
        relContratosPorBanco: () => this.request("/v1/prefeitura/relatorios/contratos-por-banco"),
        relInconsistencias: () => this.request("/v1/prefeitura/relatorios/inconsistencias"),
        // Anuência (passo 10)
        anuencia: () => this.request("/v1/prefeitura/anuencia"),
        aceitarAnuencia: (aceitoPor) => this.request("/v1/prefeitura/anuencia", { method: "POST", body: { aceito: true, aceitoPor } }),
        // Perfis + 2FA (passo 1)
        perfis: () => this.request("/v1/prefeitura/perfis"),
        salvarPerfil: (body) => this.request("/v1/prefeitura/perfis", { method: "POST", body }),
        perfilPresets: () => this.request("/v1/prefeitura/perfil-presets"),
        criarPerfilPreset: (body) => this.request("/v1/prefeitura/perfil-presets", { method: "POST", body }),
        renomearPerfilPreset: (key, nome) => this.request(`/v1/prefeitura/perfil-presets/${encodeURIComponent(key)}`, { method: "PATCH", body: { nome } }),
        excluirPerfilPreset: (key) => this.request(`/v1/prefeitura/perfil-presets/${encodeURIComponent(key)}`, { method: "DELETE" }),
        excluirPerfil: (id) => this.request(`/v1/prefeitura/perfis/${id}`, { method: "DELETE" }),
        redefinirSenhaPerfil: (id, body) => this.request(`/v1/prefeitura/perfis/${id}/redefinir-senha`, { method: "POST", body }),
        reativarPerfil: (id) => this.request(`/v1/prefeitura/perfis/${id}/reativar`, { method: "POST" }),
        rotate2fa: (id) => this.request(`/v1/prefeitura/perfis/${id}/2fa/rotate`, { method: "POST" }),
        disable2fa: (id) => this.request(`/v1/prefeitura/perfis/${id}/2fa/disable`, { method: "POST" }),
        comunicados: () => this.request("/v1/prefeitura/comunicados"),
    };
    // ============ Internal ============
    async request(path, options = {}) {
        const url = this.buildUrl(path, options.query);
        const headers = { Accept: "application/json" };
        if (!options.isFormData)
            headers["Content-Type"] = "application/json";
        if (!options.skipAuth) {
            const token = await this.storage.getAccess();
            if (token)
                headers.Authorization = `Bearer ${token}`;
        }
        // Idempotency-Key automatico em POSTs de mutacao. Se o cliente re-tenta
        // o mesmo request por flake de rede / duplo clique, backend devolve a
        // resposta cacheada (24h TTL) em vez de executar de novo. Passivo — se
        // o handler nao usa withIdempotency, nao muda nada; se usa, ganha
        // protecao automatica. Cliente pode sobrescrever passando o header
        // custom em opts.headers (nao implementado ainda, so pra referencia).
        const method = (options.method ?? "GET").toUpperCase();
        if (method === "POST" && !options.isFormData) {
            headers["Idempotency-Key"] = this.newIdempotencyKey();
        }
        const buildBody = () => {
            if (options.body === undefined)
                return undefined;
            if (options.isFormData)
                return options.body;
            return JSON.stringify(options.body);
        };
        // Retry simples pra network errors ou 5xx transientes: 1 tentativa inicial +
        // 2 retries com backoff (500ms, 1500ms). Mantem os MESMOS headers, incluindo
        // Idempotency-Key — backend detecta a duplicata e devolve resposta cacheada.
        // NAO retenta 4xx (erro do cliente — retentar nao vai corrigir).
        const doFetch = () => this.fetchImpl(url, {
            method: options.method ?? "GET",
            headers,
            body: buildBody(),
            signal: options.signal,
        });
        let res;
        let lastErr = null;
        const backoff = [0, 500, 1500];
        for (let attempt = 0; attempt < backoff.length; attempt++) {
            if (backoff[attempt])
                await new Promise((r) => setTimeout(r, backoff[attempt]));
            try {
                res = await doFetch();
                // 5xx transiente -> retry. 4xx -> nao retenta.
                if (res.status >= 500 && res.status < 600 && attempt < backoff.length - 1)
                    continue;
                break;
            }
            catch (e) {
                lastErr = e;
                // Abort do usuario -> nao retenta.
                if (e.name === "AbortError")
                    throw e;
                if (attempt === backoff.length - 1)
                    throw e;
            }
        }
        if (!res)
            throw lastErr ?? new Error("network error");
        if (res.status === 401 && !options.skipAuth) {
            const ok = await this.tryRefresh();
            if (ok) {
                const token2 = await this.storage.getAccess();
                if (token2)
                    headers.Authorization = `Bearer ${token2}`;
                res = await this.fetchImpl(url, {
                    method: options.method ?? "GET",
                    headers,
                    body: buildBody(),
                    signal: options.signal,
                });
            }
            else {
                this.opts.onAuthFailure?.();
            }
        }
        if (!res.ok) {
            let code = "http_error";
            let message = `HTTP ${res.status}`;
            let details;
            try {
                const body = (await res.json());
                if (body.error) {
                    code = body.error.code;
                    message = body.error.message;
                    details = body.error.details;
                }
            }
            catch {
                // ignore parse error
            }
            throw new ApiHttpError(res.status, code, message, details);
        }
        if (res.status === 204)
            return undefined;
        return (await res.json());
    }
    async tryRefresh() {
        if (this.refreshing) {
            await this.refreshing;
            return (await this.storage.getAccess()) !== null;
        }
        this.refreshing = (async () => {
            const refresh = await this.storage.getRefresh();
            if (!refresh)
                return;
            try {
                const res = await this.fetchImpl(this.buildUrl("/v1/auth/refresh"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ refresh_token: refresh }),
                });
                if (!res.ok) {
                    await this.storage.clear();
                    return;
                }
                const tok = (await res.json());
                await this.storage.set(tok);
            }
            catch {
                await this.storage.clear();
            }
        })();
        try {
            await this.refreshing;
        }
        finally {
            this.refreshing = null;
        }
        return (await this.storage.getAccess()) !== null;
    }
    buildUrl(path, query) {
        const url = new URL(path, this.opts.baseUrl);
        if (query) {
            for (const [k, v] of Object.entries(query)) {
                if (v === undefined || v === null)
                    continue;
                url.searchParams.set(k, String(v));
            }
        }
        return url.toString();
    }
    /** Gera uma chave de idempotencia unica por request (uuid v4). Enviada
     *  automaticamente no header 'Idempotency-Key' em todo POST. Se o request
     *  falhar por rede/timeout e o usuario re-tentar, o backend devolve a
     *  resposta cacheada (24h) em vez de duplicar a operacao. */
    newIdempotencyKey() {
        // crypto.randomUUID esta em Node 19+, Deno, browsers modernos, Workers.
        const c = globalThis.crypto;
        if (c?.randomUUID)
            return c.randomUUID();
        // Fallback pra ambientes sem crypto.randomUUID (raro): timestamp + random.
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
    }
}
//# sourceMappingURL=client.js.map